type RequestCacheEntry = {
  value: unknown;
  expiresAt: number;
  generation: number;
};

type PendingRequestEntry = {
  promise: Promise<unknown>;
  generation: number;
};

type RequestCacheDecision = {
  key: string;
  ttlMs: number;
} | null;

const DEFAULT_CACHE_TTL_MS = 4000;
const QUERY_CACHE_TTL_MS = 1500;

const responseCache = new Map<string, RequestCacheEntry>();
const pendingCache = new Map<string, PendingRequestEntry>();
let cacheGeneration = 0;

const isDev = typeof process !== "undefined" && process.env.NODE_ENV !== "production";

const debugLog = (...args: unknown[]) => {
  if (isDev) {
    // Cache logs help confirm which requests are deduped during tab switches.
    console.debug("[api-cache]", ...args);
  }
};

const normalizeBody = (body: unknown): string => {
  if (body === undefined || body === null) {
    return "";
  }

  if (typeof body === "string") {
    return body;
  }

  try {
    return JSON.stringify(body);
  } catch {
    return String(body);
  }
};

const getAuthorizationHeader = (headers?: Headers): string => {
  return headers?.get("Authorization") || "";
};

const extractCommandName = (body: unknown): string | undefined => {
  if (typeof body !== "string") {
    return undefined;
  }

  try {
    const parsed = JSON.parse(body) as { command?: unknown } | null;
    if (!parsed || typeof parsed.command !== "string") {
      return undefined;
    }

    return parsed.command;
  } catch {
    return undefined;
  }
};

const isReadOnlyQuery = (method: string, url: string, body: unknown): boolean => {
  if (method !== "POST") {
    return false;
  }

  if (!url.includes("/api/commands/Query")) {
    return false;
  }

  const commandName = extractCommandName(body);
  if (!commandName) {
    return false;
  }

  return /^(Read|read)/.test(commandName);
};

export const getRequestCacheDecision = (
  method: string,
  url: string,
  body: unknown,
  headers?: Headers
): RequestCacheDecision => {
  const normalizedMethod = method.toUpperCase();
  const authorization = getAuthorizationHeader(headers);
  const bodyKey = normalizeBody(body);

  if (normalizedMethod === "GET" || normalizedMethod === "HEAD") {
    return {
      key: `${normalizedMethod}|${url}|auth:${authorization}`,
      ttlMs: DEFAULT_CACHE_TTL_MS
    };
  }

  if (isReadOnlyQuery(normalizedMethod, url, body)) {
    return {
      key: `${normalizedMethod}|${url}|auth:${authorization}|body:${bodyKey}`,
      ttlMs: QUERY_CACHE_TTL_MS
    };
  }

  return null;
};

export const getCachedRequestValue = <T>(key: string): T | undefined => {
  const entry = responseCache.get(key);

  if (!entry) {
    return undefined;
  }

  if (entry.generation !== cacheGeneration || entry.expiresAt <= Date.now()) {
    responseCache.delete(key);
    return undefined;
  }

  debugLog("cache hit", key);
  return entry.value as T;
};

export const setCachedRequestValue = (key: string, value: unknown, ttlMs: number): void => {
  responseCache.set(key, {
    value,
    expiresAt: Date.now() + ttlMs,
    generation: cacheGeneration
  });
};

export const getPendingRequestValue = <T>(key: string): Promise<T> | undefined => {
  const entry = pendingCache.get(key);

  if (!entry || entry.generation !== cacheGeneration) {
    pendingCache.delete(key);
    return undefined;
  }

  debugLog("pending hit", key);
  return entry.promise as Promise<T>;
};

export const setPendingRequestValue = (key: string, promise: Promise<unknown>): void => {
  pendingCache.set(key, {
    promise,
    generation: cacheGeneration
  });
};

export const removePendingRequestValue = (key: string, promise?: Promise<unknown>): void => {
  const entry = pendingCache.get(key);

  if (!entry) {
    return;
  }

  if (!promise || entry.promise === promise) {
    pendingCache.delete(key);
  }
};

export const clearRequestCache = (reason?: string): void => {
  if (responseCache.size || pendingCache.size) {
    debugLog("clear", reason || "manual");
  }

  responseCache.clear();
  pendingCache.clear();
  cacheGeneration += 1;
};
